import { z } from 'zod';
import { ListingType } from '../crm/property.js';
/**
 * Single source of truth for CSV listing-row validation, shared by the API
 * (server-authoritative validation via a Nest pipe) and the admin app (client
 * pre-flight validation at preview). Keeping one schema prevents the two sides
 * from drifting — e.g. the client accepting a value the server later rejects.
 *
 * This is a generic listing concern (not CRM-specific), so it lives alongside
 * the other listing models rather than under `crm/`.
 *
 * `companyId` and `source` are intentionally omitted: the API sets them from
 * the authenticated request, never from client input.
 */
export const ListingCsvRowSchema = z.object({
    // --- The only hard requirements: these four fields must be present. ---
    address: z.string().trim().min(1, 'address is required').max(500),
    monthlyRentDollars: z.coerce.number({
        invalid_type_error: 'monthly_rent must be a number',
    }),
    beds: z.coerce.number({ invalid_type_error: 'beds must be a number' }),
    baths: z.coerce.number({ invalid_type_error: 'baths must be a number' }),
    // --- Everything below is optional and accepted leniently: no format/enum/URL
    // enforcement. Values that can't be stored (unknown `type`, unparseable
    // date/number) are dropped by the client before upload rather than rejected;
    // lengths are capped only to fit the DB columns. ---
    unit: z.string().trim().max(100).optional(),
    type: z.nativeEnum(ListingType).optional(),
    sqft: z.coerce.number().optional(),
    isAvailable: z.boolean().optional(),
    availabilityDate: z.coerce.date().optional(),
    detailsUrl: z.string().trim().max(2048).optional(),
    showingUrl: z.string().trim().max(2048).optional(),
    imgUrl: z.string().trim().max(2048).optional(),
    petPolicy: z.string().trim().optional(),
    dogsAllowed: z.boolean().optional(),
    catsAllowed: z.boolean().optional(),
    hasParking: z.boolean().optional(),
    housingVouchersAccepted: z.boolean().optional(),
    housingVouchersPolicy: z.string().trim().optional(),
    shortDescription: z.string().trim().optional(),
    longDescription: z.string().trim().optional(),
    externalId: z.string().trim().max(500).optional(),
});
/**
 * CSV upload request payload. The upload replaces all listings for the company
 * (alternative, mutually-exclusive sync mode).
 */
export const UploadListingsCsvSchema = z.object({
    listings: z
        .array(ListingCsvRowSchema)
        .min(1, 'At least one listing is required')
        .max(25000, 'A single upload may contain at most 25,000 listings'),
});
//# sourceMappingURL=listing-csv.js.map